import { Component, inject, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { ModalService } from '@card-software/library';
import { EnumValues } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ShiftsRoles } from '@app/models/roles/roles-group';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { UserAuthenticationService } from '@app/services/user-authentication.service';
import { CurrentShift } from '../../models/shift/current-shift';
import { ShiftProxyService } from '../../proxies/production/shift.proxy.service';
import { EnumService } from '../../services/enum.service';
import { CurrentShiftModalComponent } from '../shared/_modals/current-shift-modal/current-shift-modal.component';

@Component({
  selector: 'app-current-shift',
  templateUrl: './current-shift.component.html',
  styleUrl: './current-shift.component.scss',
})
export class CurrentShiftComponent implements OnInit {
  private readonly shiftProxyService = inject(ShiftProxyService);
  private readonly modalService = inject(ModalService);
  private readonly enumService = inject(EnumService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);
  private readonly localeHelperService = inject(LocaleHelperService);

  currentShift: CurrentShift | null = null;
  isLoading = signal(0);
  shiftEnum: EnumValues = this.enumService.getEnum('Shift', 'productionEnums')?.values || {};

  private updateLoading(delta: number): void {
    this.isLoading.update((value) => Math.max(value + delta, 0));
  }

  get isShiftsAdmin(): boolean {
    return this.userAuthenticationService.roles.includes(ShiftsRoles.ShiftsAdmin);
  }

  ngOnInit(): void {
    this.loadCurrentShift();
  }

  loadCurrentShift(): void {
    this.updateLoading(1);
    this.shiftProxyService
      .getCurrentShift()
      .pipe(finalize(() => this.updateLoading(-1)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.currentShift = response.data;
          }
        },
      });
  }

  getShiftLabel(shiftValue: number): string {
    return this.shiftEnum[shiftValue] || 'Unknown';
  }

  openEditModal(): void {
    if (!this.currentShift) {
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('CurrentShift.no_current_shift_selected', 'No current shift selected'),
      );
      return;
    }

    const modalData = {
      currentShift: this.currentShift,
      shiftEnum: this.shiftEnum,
    };

    this.modalService.open(CurrentShiftModalComponent, { modalClass: 'modal_small' }, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'save' && result.data) {
          this.updateShift(result.data);
        }
      },
    });
  }

  updateShift(updatedShift: CurrentShift): void {
    if (!updatedShift.id) {
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('CurrentShift.shift_id_required', 'Shift id is required'),
      );
      return;
    }

    this.updateLoading(1);
    this.shiftProxyService
      .updateShift(updatedShift.id, updatedShift)
      .pipe(finalize(() => this.updateLoading(-1)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.currentShift = response.data;
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
              this.localeHelperService.translateWithFallback('CurrentShift.shift_updated_successfully', 'Shift updated successfully'),
            );
          }
        },
      });
  }
}
